import type { ComponentProps, FC } from "react";

import { MenuItem, Tooltip } from "@hightouch/htui";
import pluralize from "pluralize";

import {
  type UpdatableResource,
  useCanUpdateResources,
} from "./use-can-update-resources";

type Props = ComponentProps<typeof MenuItem> & {
  resources: UpdatableResource[];
  /** Singular noun for the selected resources, used in the unauthorized tooltip. */
  resourceLabel: string;
};

/*
 * A menu item for a bulk action that is disabled unless the user can update
 * every selected resource, so the action is never offered when it would fail.
 */
export const BulkUpdateMenuItem: FC<Readonly<Props>> = ({
  resources,
  resourceLabel,
  isDisabled,
  ...props
}) => {
  const { isPermitted, isLoading } = useCanUpdateResources(resources);

  return (
    <Tooltip
      isDisabled={isPermitted || isLoading}
      message={`You do not have permission to update ${
        resources.length === 1
          ? `this ${resourceLabel}`
          : `one or more of the selected ${pluralize(resourceLabel)}`
      }`}
    >
      <MenuItem {...props} isDisabled={!isPermitted || isDisabled} />
    </Tooltip>
  );
};
